import { useMutation, useQueryClient } from '@tanstack/react-query'
import { assignCandidateRole, removeCandidateRole, candidateKeys } from '@/services/candidates'
import type { CandidateRole } from '@/types'

interface AssignRoleParams {
  role: CandidateRole
  districtId?: string
}

export function useAssignCandidateRole(candidateId: string, partyId: string) {
  const queryClient = useQueryClient()

  const assignMutation = useMutation({
    mutationFn: ({ role, districtId }: AssignRoleParams) =>
      assignCandidateRole(candidateId, partyId, role, districtId),
    onSuccess: (data) => {
      // Invalidate candidate detail queries
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.detailWithParty(candidateId) })

      // Invalidate party-level queries
      queryClient.invalidateQueries({ queryKey: candidateKeys.byParty(partyId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.activeByParty(partyId) })

      // Invalidate role-specific queries
      queryClient.invalidateQueries({ queryKey: candidateKeys.partyLeader(partyId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.regionalCandidates(partyId) })

      // Invalidate district candidate if assigned to a district
      if (data.assigned_district_id) {
        queryClient.invalidateQueries({
          queryKey: candidateKeys.districtCandidate(partyId, data.assigned_district_id),
        })
      }
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => removeCandidateRole(candidateId),
    onSuccess: () => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: candidateKeys.detail(candidateId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.detailWithParty(candidateId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.byParty(partyId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.activeByParty(partyId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.partyLeader(partyId) })
      queryClient.invalidateQueries({ queryKey: candidateKeys.regionalCandidates(partyId) })
    },
  })

  return {
    assignRole: assignMutation.mutate,
    assignRoleAsync: assignMutation.mutateAsync,
    isAssigning: assignMutation.isPending,
    assignError: assignMutation.error,

    removeRole: removeMutation.mutate,
    removeRoleAsync: removeMutation.mutateAsync,
    isRemoving: removeMutation.isPending,
    removeError: removeMutation.error,
  }
}
